/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api-client';
import {
	TApiResponse,
	TMeterPoint,
	TParsedMeterPoint,
} from '../types';
import { TApiParams, TParams } from '@global-types/index';
import { NOT_APPLICABLE } from '@config/constants/common';
import { formatDate } from '@utils/functions';
import { getUnitLabel } from '@utils/units';
import { getCountryName } from '@utils/countries';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getMeterPoints = (
	params: TApiParams
): Promise<TApiResponse> => {
	const { queryParams, sortParams, page, pageSize } =
		params;
	return apiClient.get('/meter_points', {
		params: {
			page: page,
			pageSize: pageSize,
			query: queryParams,
			sort: sortParams,
			populate: [
				'project',
				'site',
				'entity',
				'organization',
			],
		},
	});
};

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */
export const useMeterPointsApi = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { data, isFetching, isFetched } = useQuery({
		queryKey: [
			'meter-points-projects',
			{
				page: page,
				size: size,
				queryParams: queryParams,
				sortParams: sortParams,
			},
		],
		queryFn: () =>
			getMeterPoints({
				page: page,
				pageSize: size,
				queryParams: queryParams,
				sortParams: sortParams,
			}),
	});

	const updateList = (meterPoints: TMeterPoint[]) =>
		meterPoints.reduce(
			(
				acc: TParsedMeterPoint[],
				currentItem: TMeterPoint
			) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const modifiedItem: any = {
					id: currentItem.id,
					site: currentItem.site
						? currentItem.site.name
						: NOT_APPLICABLE,
					idSite: currentItem.site
						? currentItem.site.id
						: NOT_APPLICABLE,
					idProject: currentItem.project
						? currentItem.project.id
						: NOT_APPLICABLE,
					idEntity: currentItem.entity
						? currentItem.entity.id
						: NOT_APPLICABLE,
					project: currentItem.project
						? currentItem.project.name
						: NOT_APPLICABLE,
					place:
						currentItem.location &&
						currentItem.location.place
							? currentItem.location.place
							: NOT_APPLICABLE,
					countryCode:
						currentItem.location &&
						currentItem.location.countryCode
							? getCountryName(
									currentItem.location.countryCode
								)
							: NOT_APPLICABLE,
					creationDate: formatDate(
						currentItem.createdAt,
						'DD/MM/YYYY'
					),
					serialNumber: currentItem.serialNumber
						? currentItem.serialNumber
						: NOT_APPLICABLE,
					unit: currentItem.unit
						? getUnitLabel(currentItem.unit.toLowerCase())
						: NOT_APPLICABLE,
					organization: currentItem.organization
						? currentItem.organization.name
						: NOT_APPLICABLE,
					producer: currentItem.entity
						? currentItem.entity.name
						: NOT_APPLICABLE,
					createdAt: formatDate(
						currentItem.createdAt,
						'DD/MM/YYYY'
					),
					location: currentItem.location
						? currentItem.location
						: { countryCode: '', place: '' },
				};
				acc.push(modifiedItem);

				return acc;
			},
			[]
		);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isFetched,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
